import { Component, ElementRef, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import OverlayScrollbars from 'overlayscrollbars';
import moment from 'moment';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ChallengesService } from './challenges.service';
import { ModuleManagerRefreshType } from "src/shared/models/module-manager/module-manager-options.model";
import { DialogService } from 'primeng/dynamicdialog';
import { CxoneChallengeHistoryModalComponent } from './cxone-challenge-history-modal/cxone-challenge-history-modal.component';
import { CxoneChallengeCreateModalComponent } from './cxone-challenge-create-modal/cxone-challenge-create-modal.component';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { OnDestroy } from 'cv-ng-metadata/core';
import { ChallengeHistory } from './challenge.model';

@Component({
    selector: "cxone-challenges",
    templateUrl: "./cxone-challenges.component.html",
    styleUrl: "./cxone-challenges.component.scss",
    providers: [DialogService],
})
export class CxoneChallengesComponent implements OnInit, OnDestroy {
    localizationStrings: Map<string, string> = new Map<string, string>();
    isLoading = false;
    isWidgetSmall = true;
    scrollSize = 0;
    firstLoad = true;
    endedChallenges: ChallengeHistory[] = null;
    private resizeTimeout;
    @ViewChild("challengesContainer") challengesContainerRef: ElementRef;
    @ViewChild("header") headerRef: ElementRef;

    @Input() moduleKey: string;
    @Input() moduleManager: ModuleManager;
    public noPMLicense: boolean = false;
    @HostListener("window:resize", ["$event"])
    onResize() {
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }

        this.resizeTimeout = setTimeout(() => {
            if (!this.firstLoad && !this.moduleManager.showLoader) {
                this.getScrollSize();
                this.isWidgetSmall = this.moduleManager.getSize().y < 11;
            }
        }, 500);
    }

    constructor(
        public challengeService: ChallengesService,
        private dialogSrc: DialogService,
        private cvLocalizationService: CVLocalizationService,
        private navigationService: NavigationService
    ) {}

    async ngOnInit() {
        this.noPMLicense = !this.navigationService.hasPartnerPMAccess;
        this.moduleManager.showLoader = true;
        this.moduleManager.registerEvents({
            onResize: () => {
                this.isWidgetSmall = this.moduleManager.getSize().y < 11;
                if (!this.firstLoad && !this.moduleManager.showLoader) {
                    this.getScrollSize();
                }
            },
        });
        this.loadLocalization();
        this.moduleManager.setOptions({
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.FiveMinute,
            onRefresh: async (isAutoRefresh) => {
                this.moduleManager.showLoader = !isAutoRefresh;
                this.isLoading = true;
                await this.challengeService.loadChallenges(
                    isAutoRefresh,
                    this.moduleKey,
                    this.moduleManager,
                    () => {
                        this.isLoading = false;
                        this.filterChallenges(this.getScrollSize.bind(this));
                    }
                );
            },
            canRefresh: () => {
                return !this.moduleManager.showLoader;
            },
        });
        this.isLoading = true;
        this.challengeService.loadChallenges(
            false,
            this.moduleKey,
            this.moduleManager,
            () => {
                this.isLoading = false;
                this.firstLoad = false;
                setTimeout(() => {
                    this.filterChallenges(this.getScrollSize.bind(this));
                }, 0);
            }
        );
    }

    ngOnDestroy() {
        this.moduleManager.registerEvents(false);
    }

    getScrollSize() {
        const headerHeight = this.headerRef?.nativeElement?.offsetHeight ?? 0;
        const challengesContainerHeight =
            this.challengesContainerRef?.nativeElement?.offsetHeight ?? 0;
        this.scrollSize = challengesContainerHeight - headerHeight - 14;
    }

    get hasActiveChallenges() {
        if (!this.challengeService.challenges) return false;
        if (
            this.challengeService.challenges.activeChallenges.RunningChallenges
                .length === 0 &&
            this.challengeService.challenges.activeChallenges.UpcomingChallenges
                .length === 0 &&
            (this.endedChallenges && this.endedChallenges.length === 0)
        ) {
            return false;
        } else {
            return true;
        }
    }

    get isLoaderValid(): boolean {
        if (this.firstLoad) {
            return !this.moduleManager.showLoader;
        } else {
            return true;
        }
    }

    get hasPendingChallenges(): boolean {
        return (
            this.challengeService?.challenges?.pendingChallenges?.Invitations
                ?.length > 0
        );
    }

    get showEmptyState(): boolean {
        return (
            !this.hasActiveChallenges &&
            !this.moduleManager.showLoader &&
            this.challengeService?.challenges?.pendingChallenges?.Invitations
                .length === 0 &&
            this.endedChallenges !== null
        );
    }

    scrollOptions: OverlayScrollbars.PartialOptions = {
        scrollbars: {
            autoHide: "leave",
            autoHideDelay: 100,
            theme: "scrollbar-challenge",
        },
    };

    hiddenScrollOptions: OverlayScrollbars.PartialOptions = {
        scrollbars: {
            autoHide: "leave",
            visibility: "hidden",
            autoHideDelay: 100,
            theme: "scrollbar-challenge",
        },
    };

    async loadLocalization() {
        this.localizationStrings = await this.cvLocalizationService.GetSection(
            "Challenges"
        );
    }

    getLocalizationString(key: string, defaultValue: string): string {
        return this.localizationStrings.has(key)
            ? this.localizationStrings.get(key)
            : defaultValue;
    }

    openHistoryModal() {
        this.dialogSrc.open(CxoneChallengeHistoryModalComponent, {
            width: "700px",
            height: "500px",
            baseZIndex: 10000,
            styleClass:
                "cx-one-ui challenge-participants-modal btn-bg-is-white",
            data: {
                challenges: this.challengeService.challenges.historyChallenges,
                headerTitle: this.getLocalizationString(
                    "Challenge_History",
                    "Challenges History"
                ),
                saveLabel: this.getLocalizationString("Close", "Close"),
                getLocalizationString: this.getLocalizationString.bind(this),
            },
        });
    }

    openCreateChallengeModal() {
        this.dialogSrc.open(CxoneChallengeCreateModalComponent, {
            width: "364px",
            height: "536px",
            baseZIndex: 10000,
            styleClass:
                "cx-one-ui challenge-participants-modal btn-bg-is-white",
            data: {
                headerTitle: this.cvLocalizationService.getCachedText(
                    "Challenges",
                    "Create_Challenge_Title",
                    "Create Challenge"
                ),
                saveLabel: this.cvLocalizationService.getCachedText(
                    "Common",
                    "Create",
                    "Create"
                ),
                cancelLabel: this.cvLocalizationService.getCachedText(
                    "Common",
                    "Cancel_Button",
                    "Cancel"
                ),
            },
        });
    }

    filterChallenges(callback: () => void = () => {}) {
        if (this.challengeService?.challenges?.historyChallenges?.length > 0) {
            const currentDate = moment();
            const twoWeeksAgo = currentDate.clone().subtract(14, "days");
            this.endedChallenges =
                this.challengeService.challenges.historyChallenges.filter(
                    (challenge) =>
                        moment(challenge.EndDate).isAfter(twoWeeksAgo)
                );
        } else {
            this.endedChallenges = [];
        }
        callback();
    }

    get createLabel(): string {
        return this.cvLocalizationService.getCachedText(
            "Common",
            "Create",
            "Create"
        );
    }

    get historyLabel(): string {
        return this.cvLocalizationService.getCachedText(
            "Challenges",
            "History",
            "History"
        );
    }
}
